import { z } from 'zod';

import { cursorPaginationQuery } from './pagination.js';
import {
  WORK_ORDER_ATTACHMENT_KINDS,
  WORK_ORDER_CATEGORIES,
  WORK_ORDER_OPEN_STATUSES,
  WORK_ORDER_SEVERITIES,
  WORK_ORDER_STATUSES,
  WORK_ORDER_TRANSITIONS,
  type WorkOrderStatus,
} from './work-order-constants.js';

/**
 * Phase 2 — work-order request/response schemas. Every endpoint that
 * accepts user input parses through one of these (via the
 * `validate({ body | query | params })` middleware).
 */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

const woMetadata = z
  .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .optional()
  .default({});

/** Coerce a comma-separated query-string value into an array of T. */
function csvList<T extends z.ZodTypeAny>(item: T) {
  return z
    .union([z.array(item), z.string()])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      if (Array.isArray(v)) return v;
      const parts = v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return parts as unknown as z.infer<T>[];
    })
    .pipe(z.array(item).optional());
}

/** Optional lead time for a due-date reminder (days before `dueAt`). */
export const remindBeforeDaysSchema = z
  .union([z.literal(1), z.literal(2), z.literal(3)])
  .nullish()
  .transform((v) => v ?? null);

export const createWorkOrderInput = z.object({
  buildingId: z.string().uuid(),
  unitId: z.string().uuid().nullish().transform((v) => v ?? null),
  title: z.string().trim().min(1).max(200),
  description: optionalText(8_000),
  category: z.enum(WORK_ORDER_CATEGORIES).default('other'),
  severity: z.enum(WORK_ORDER_SEVERITIES).optional(),
  assignedTeamId: z.string().uuid().nullish().transform((v) => v ?? null),
  assignedUserId: z.string().uuid().nullish().transform((v) => v ?? null),
  dueAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .transform((v) => v ?? null),
  /** When set with `dueAt`, creates a reminder that fires N days before due. */
  remindBeforeDays: remindBeforeDaysSchema.optional(),
  /** Upload IDs (from `/uploads/presign`) that have already been
   *  uploaded; the server links them as `photo` attachments and emits
   *  `photo_added` timeline events. */
  attachmentUploadIds: z.array(z.string().uuid()).max(20).optional().default([]),
  /** Optional voice memo captured in the create sheet. Linked as a
   *  `voice_memo` attachment (not a photo) with its Whisper transcript
   *  persisted on the row so the WO timeline can render it. */
  voiceMemo: z
    .object({
      uploadId: z.string().uuid(),
      transcript: z.string().trim().max(20_000).optional(),
    })
    .optional(),
  /** Pest / unit-visit WOs: creates one extermination_log per unit. */
  unitLogUnitIds: z.array(z.string().uuid()).max(500).optional(),
  metadata: woMetadata,
});
export type CreateWorkOrderInput = z.infer<typeof createWorkOrderInput>;

export const updateWorkOrderInput = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: optionalText(8_000).optional(),
    category: z.enum(WORK_ORDER_CATEGORIES).optional(),
    severity: z.enum(WORK_ORDER_SEVERITIES).optional(),
    unitId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    dueAt: z
      .string()
      .datetime({ offset: true })
      .nullish()
      .transform((v) => v ?? null)
      .optional(),
    remindBeforeDays: remindBeforeDaysSchema.optional(),
    metadata: woMetadata.optional(),
  })
  .refine(
    (v) => Object.values(v).some((x) => x !== undefined),
    { message: 'Provide at least one field to update' },
  );
export type UpdateWorkOrderInput = z.infer<typeof updateWorkOrderInput>;

export const transitionWorkOrderInput = z.object({
  toStatus: z.enum(WORK_ORDER_STATUSES),
  notes: optionalText(2_000),
  /** Used when transitioning to `cancelled`. */
  cancellationReason: optionalText(500),
  /** Used when transitioning to `completed`. */
  completionNotes: optionalText(2_000),
});
export type TransitionWorkOrderInput = z.infer<typeof transitionWorkOrderInput>;

export const assignWorkOrderInput = z
  .object({
    teamId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    userId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    vendorId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
    notes: optionalText(2_000),
  })
  .refine(
    (v) =>
      v.teamId !== undefined ||
      v.userId !== undefined ||
      v.vendorId !== undefined,
    {
      message: 'Provide at least one of teamId, userId, or vendorId',
    },
  );
export type AssignWorkOrderInput = z.infer<typeof assignWorkOrderInput>;

export const commentInput = z.object({
  body: z.string().trim().min(1).max(8_000),
});
export type CommentInput = z.infer<typeof commentInput>;

export const bulkTransitionInput = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
  toStatus: z.enum(WORK_ORDER_STATUSES),
  notes: optionalText(2_000),
});
export type BulkTransitionInput = z.infer<typeof bulkTransitionInput>;

export const workOrderFiltersQuery = cursorPaginationQuery.extend({
  status: csvList(z.enum(WORK_ORDER_STATUSES)),
  severity: csvList(z.enum(WORK_ORDER_SEVERITIES)),
  category: csvList(z.enum(WORK_ORDER_CATEGORIES)),
  buildingId: z.string().uuid().optional(),
  unitId: z.string().uuid().optional(),
  assignedUserId: z.string().uuid().optional(),
  assignedTeamId: z.string().uuid().optional(),
  /** Filter to "my open" — when present, the API substitutes the
   *  caller's user id and ignores `assignedUserId`. */
  mine: z.coerce.boolean().optional(),
  q: z.string().trim().max(120).optional(),
  dueBefore: z.string().datetime({ offset: true }).optional(),
  dueAfter: z.string().datetime({ offset: true }).optional(),
  createdAfter: z.string().datetime({ offset: true }).optional(),
  submittedVia: z.string().optional(),
  sort: z
    .enum(['-created_at', 'due_at', 'severity', '-updated_at', '-number'])
    .optional()
    .default('-created_at'),
});
export type WorkOrderFiltersQuery = z.infer<typeof workOrderFiltersQuery>;

export const attachmentPresignInput = z.object({
  kind: z.enum(WORK_ORDER_ATTACHMENT_KINDS).default('photo'),
  filename: z.string().trim().min(1).max(255),
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Invalid content type'),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
  caption: optionalText(500),
});
export type AttachmentPresignInput = z.infer<typeof attachmentPresignInput>;

/** Posted after the client successfully PUTs the file to Spaces. */
export const attachmentConfirmInput = z.object({
  caption: optionalText(500).optional(),
});
export type AttachmentConfirmInput = z.infer<typeof attachmentConfirmInput>;

/**
 * Pure transition validator usable from both API and UI. Returns null
 * on success or a stable error code on failure. The caller is
 * responsible for converting the failure into an HTTP error or a
 * disabled control.
 */
export type TransitionFailure =
  | { code: 'CANCELLED_TERMINAL'; message: string }
  | { code: 'INVALID_TRANSITION'; message: string }
  | { code: 'REOPEN_REQUIRES_ADMIN'; message: string }
  | { code: 'PHOTO_REQUIRED'; message: string };

export interface TransitionContext {
  fromStatus: WorkOrderStatus;
  toStatus: WorkOrderStatus;
  /** Caller's role string. `'admin'` is the only role permitted to
   *  reopen a completed work order via `completed → in_progress`. */
  role: string;
  /** Whether the work order has at least one photo attachment.
   *  Required for `* → completed` when the org setting is on. */
  hasPhoto: boolean;
  /** Org-level setting copy. */
  requirePhotoOnCompletion: boolean;
}

export function validateTransition(ctx: TransitionContext): TransitionFailure | null {
  const allowed = WORK_ORDER_TRANSITIONS[ctx.fromStatus];
  if (ctx.fromStatus === 'cancelled') {
    return {
      code: 'CANCELLED_TERMINAL',
      message: 'Cancelled work orders cannot transition to another status',
    };
  }
  if (!allowed.includes(ctx.toStatus)) {
    return {
      code: 'INVALID_TRANSITION',
      message: `Cannot move work order from "${ctx.fromStatus}" to "${ctx.toStatus}"`,
    };
  }
  if (ctx.fromStatus === 'completed' && ctx.toStatus === 'in_progress') {
    if (ctx.role !== 'admin') {
      return {
        code: 'REOPEN_REQUIRES_ADMIN',
        message: 'Only admins can reopen a completed work order',
      };
    }
  }
  if (
    ctx.toStatus === 'completed' &&
    ctx.requirePhotoOnCompletion &&
    !ctx.hasPhoto
  ) {
    return {
      code: 'PHOTO_REQUIRED',
      message: 'A completion photo is required before this work order can be marked complete',
    };
  }
  return null;
}

/** True if the status is "open" — used by dashboards and badges. */
export function isOpenStatus(status: WorkOrderStatus): boolean {
  return (WORK_ORDER_OPEN_STATUSES as readonly string[]).includes(status);
}
